import { apiRequest } from './client';
import type {
  AccessGrant,
  AuditEvent,
  Cohort,
  HealthResponse,
  Principal,
  ReadinessFinding,
  SessionState,
  WorkshopSource,
} from './types';

/**
 * Every API call the app can make, in one place, named after the requirement
 * it serves. Components never build a path string themselves.
 *
 * Endpoints the Worker has not implemented yet answer `501`; they are declared
 * here anyway so the screens that use them are written against the final shape
 * rather than a placeholder, and so the remaining surface is visible in one
 * file. See worker/README.md for what is live today.
 */
export const api = {
  /** Liveness probe; also the cheapest way to tell the API apart from Pages. */
  health: () => apiRequest<HealthResponse>('/health'),

  /** Who the server believes is calling (US-1.1). Drives cosmetic UI gating. */
  me: () => apiRequest<{ principal: Principal | null }>('/me'),

  sources: {
    /** US-2.1 */
    list: () => apiRequest<{ sources: WorkshopSource[] }>('/admin/sources'),
    register: (input: { repository: string; tag: string }) =>
      apiRequest<WorkshopSource>('/admin/sources', { method: 'POST', body: input }),
    /** US-2.2 — server-side fetch of the pinned tag. */
    sync: (sourceId: string) =>
      apiRequest<WorkshopSource>(`/admin/sources/${sourceId}/sync`, { method: 'POST' }),
    /** US-2.3 */
    readiness: (sourceId: string) =>
      apiRequest<{ findings: ReadinessFinding[] }>(`/admin/sources/${sourceId}/readiness`),
    recheck: (sourceId: string) =>
      apiRequest<{ findings: ReadinessFinding[] }>(`/admin/sources/${sourceId}/readiness`, {
        method: 'POST',
      }),
  },

  cohorts: {
    /** US-3.1 / US-6.1 — the one admin endpoint wired end to end today. */
    list: () => apiRequest<{ cohorts: Cohort[] }>('/admin/cohorts'),
    get: (cohortId: string) => apiRequest<Cohort>(`/admin/cohorts/${cohortId}`),
    create: (input: Omit<Cohort, 'id' | 'state'>) =>
      apiRequest<Cohort>('/admin/cohorts', { method: 'POST', body: input }),
    update: (cohortId: string, input: Partial<Omit<Cohort, 'id'>>) =>
      apiRequest<Cohort>(`/admin/cohorts/${cohortId}`, { method: 'PATCH', body: input }),
  },

  grants: {
    /** US-3.4 */
    list: (cohortId: string) =>
      apiRequest<{ grants: AccessGrant[] }>(`/admin/cohorts/${cohortId}/grants`),
    /** US-3.3 */
    create: (cohortId: string, input: { attendeeIdentity: string; startsAt: string; expiresAt: string }) =>
      apiRequest<AccessGrant>(`/admin/cohorts/${cohortId}/grants`, { method: 'POST', body: input }),
    /** US-3.4 — revocation is immediate and carries a reason. */
    revoke: (cohortId: string, grantId: string, reason: string) =>
      apiRequest<AccessGrant>(`/admin/cohorts/${cohortId}/grants/${grantId}`, {
        method: 'DELETE',
        body: { reason },
      }),
  },

  /** US-6.2 — append-only, readable by admins and platform owners. */
  audit: {
    list: (params: { cohortId?: string; limit?: number } = {}) => {
      const query = new URLSearchParams();
      if (params.cohortId) query.set('cohortId', params.cohortId);
      if (params.limit) query.set('limit', String(params.limit));
      const suffix = query.size > 0 ? `?${query.toString()}` : '';
      return apiRequest<{ events: AuditEvent[] }>(`/admin/audit${suffix}`);
    },
  },

  session: {
    /** US-4.2 — polled by attendees in follow mode (ADR 0001: polling, not sockets). */
    get: (cohortId: string, signal?: AbortSignal) =>
      apiRequest<SessionState>(`/session/${cohortId}`, signal ? { signal } : {}),
    /** US-4.1/US-4.2 — presenter publishes a new position. */
    advance: (cohortId: string, position: { module: number; topic: number }) =>
      apiRequest<SessionState>(`/session/${cohortId}`, { method: 'PUT', body: position }),
  },
};
